import { useState, useRef, useEffect, useCallback } from 'react';
import { NavLink } from 'react-router-dom';
import { LuBell, LuX, LuMenu } from 'react-icons/lu';
import { LiaCheckDoubleSolid } from 'react-icons/lia';
import BellIcon from '../../icons/BellIcon';
import DashboardIcon from '../../icons/DashboardIcon';
import DollarSackIcon from '../../icons/DollarSackIcon';
import GavelIcon from '../../icons/GavelIcon';
import HexagonExclamationIcon from '../../icons/HexagonExclamationIcon';
import PackageIcon from '../../icons/PackageIcon';
import UsersIcon from '../../icons/UsersIcon';
import OrdersIcon from '../../icons/OrdersIcon';
import {
  fetchNotifications,
  markNotificationRead,
  markAllNotificationsRead,
  deleteNotification,
} from '../../lib/directus';
import { playNotificationSound } from '../../lib/notificationSound';
import styles from './AdminLayout.module.css';

const NAV_ITEMS = [
  { label: 'Dashboard',       path: '/dashboard',      Icon: DashboardIcon },
  { label: 'Partners',        path: '/partners',        Icon: UsersIcon },
  { label: 'Batch approvals', path: '/batch-approvals', Icon: PackageIcon },
  { label: 'Orders',          path: '/orders',          Icon: OrdersIcon },
  { label: 'Bid selection',   path: '/bid-selection',   Icon: GavelIcon },
  { label: 'Payments',        path: '/settlements',     Icon: DollarSackIcon },
  { label: 'Disputes',        path: '/disputes',        Icon: HexagonExclamationIcon },
];

function formatNotifTime(dateStr) {
  if (!dateStr) return '';
  return new Date(dateStr)
    .toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
    .replace(',', ' at');
}

export default function AdminLayout({ title = 'Dashboard', children }) {
  const [menuOpen, setMenuOpen]       = useState(false);
  const [bellOpen, setBellOpen]       = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [logoutModal, setLogoutModal] = useState(false);
  const [notifications, setNotifications] = useState([]);

  const menuRef    = useRef(null);
  const bellRef    = useRef(null);
  const seenIdsRef = useRef(null);

  const loadNotifications = useCallback(() => {
    fetchNotifications()
      .then(data => {
        const mapped = data.map(n => ({
          id:      n.id,
          message: n.message || n.subject || '',
          time:    n.timestamp || n.date_created || '',
          read:    n.status === 'archived',
        }));

        if (seenIdsRef.current !== null) {
          const hasNew = mapped.some(n => !n.read && !seenIdsRef.current.has(n.id));
          if (hasNew) playNotificationSound();
        }
        seenIdsRef.current = new Set(mapped.map(n => n.id));
        setNotifications(mapped);
      })
      .catch(console.error);
  }, []);

  useEffect(() => {
    loadNotifications();
    const interval = setInterval(loadNotifications, 30_000);
    return () => clearInterval(interval);
  }, [loadNotifications]);

  useEffect(() => {
    function handleClick(e) {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
      if (bellRef.current && !bellRef.current.contains(e.target)) setBellOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  async function markAllRead() {
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    try { await markAllNotificationsRead(); } catch (err) { console.error(err); }
  }

  async function handleNotifClick(n) {
    if (n.read) return;
    setNotifications(prev => prev.map(x => x.id === n.id ? { ...x, read: true } : x));
    try { await markNotificationRead(n.id); } catch (err) { console.error(err); }
  }

  async function handleDeleteNotif(e, id) {
    e.stopPropagation();
    setNotifications(prev => prev.filter(x => x.id !== id));
    try { await deleteNotification(id); } catch (err) { console.error(err); }
  }

  function handleLogout() {
    localStorage.clear();
    window.location.replace('/login');
  }

  const hasUnread = notifications.some(n => !n.read);

  return (
    <div className={styles.shell}>
      {sidebarOpen && (
        <div className={styles.sidebarOverlay} onClick={() => setSidebarOpen(false)} />
      )}

      <aside className={`${styles.sidebar} ${sidebarOpen ? styles.sidebarMobileOpen : ''}`}>
        <div className={styles.sidebarLogo}>
          <img src="/driftwood-logo.png" alt="Driftwood Agri" />
        </div>
        <nav>
          {NAV_ITEMS.map(({ path, label, Icon }) => (
            <NavLink
              key={path}
              to={path}
              onClick={() => setSidebarOpen(false)}
              className={({ isActive }) =>
                `${styles.navItem} ${isActive ? styles.navItemActive : ''}`
              }
            >
              {({ isActive }) => (
                <>
                  <Icon active={isActive} />
                  {label}
                </>
              )}
            </NavLink>
          ))}
        </nav>
      </aside>

      <div className={styles.main}>
        <header className={styles.header}>
          <div className={styles.headerLeft}>
            <button
              className={styles.hamburger}
              onClick={() => setSidebarOpen(o => !o)}
              aria-label="Open navigation"
            >
              <LuMenu size={22} />
            </button>
            <h1 className={styles.headerTitle}>{title}</h1>
          </div>

          <div className={styles.headerActions}>
            {/* Bell */}
            <div ref={bellRef} style={{ position: 'relative' }}>
              <button
                className={styles.bellBtn}
                onClick={() => { setBellOpen(o => !o); setMenuOpen(false); }}
                aria-label="Notifications"
              >
                <BellIcon />
                {hasUnread && <span className={styles.bellDot} />}
              </button>

              {bellOpen && (
                <div className={styles.notificationsPanel}>
                  <div className={styles.notifHeader}>
                    <p className={styles.notifTitle}>Notifications</p>
                    <button className={styles.notifClose} onClick={() => setBellOpen(false)}>
                      <LuX size={18} />
                    </button>
                  </div>
                  <div className={styles.markReadRow}>
                    <button className={styles.markRead} onClick={markAllRead}>
                      <LiaCheckDoubleSolid size={16} style={{ marginRight: 4 }} />
                      Mark all as read
                    </button>
                  </div>
                  {notifications.length === 0 && (
                    <div className={styles.notifEmpty}>No notifications</div>
                  )}
                  <div className={styles.notifList}>
                    {notifications.map(n => (
                      <div
                        key={n.id}
                        className={styles.notifItem}
                        onClick={() => handleNotifClick(n)}
                        style={{ cursor: n.read ? 'default' : 'pointer' }}
                      >
                        <div className={styles.notifIconWrap}>
                          <LuBell size={16} className={n.read ? styles.notifIconRead : styles.notifIconUnread} />
                        </div>
                        <div style={{ flex: 1 }}>
                          <p className={n.read ? styles.notifTextRead : styles.notifTextUnread}>{n.message}</p>
                          <p className={styles.notifTime}>{formatNotifTime(n.time)}</p>
                        </div>
                        {!n.read && <span className={styles.notifDot} />}
                        <button className={styles.notifDeleteBtn} onClick={(e) => handleDeleteNotif(e, n.id)}>
                          <LuX size={13} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Avatar */}
            <div className={styles.avatarWrapper} ref={menuRef}>
              <img
                src="avatar-holder-image.jpg"
                alt="User avatar"
                className={styles.avatar}
                onClick={() => { setMenuOpen(o => !o); setBellOpen(false); }}
              />
              {menuOpen && (
                <div className={styles.avatarMenu}>
                  <button
                    className={styles.avatarMenuItem}
                    onClick={() => { setMenuOpen(false); setLogoutModal(true); }}
                  >
                    Log out
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        <main className={styles.content}>
          {children}
        </main>
      </div>

      {logoutModal && (
        <div className={styles.modalOverlay} onClick={() => setLogoutModal(false)}>
          <div className={styles.modal} onClick={e => e.stopPropagation()}>
            <button className={styles.modalClose} onClick={() => setLogoutModal(false)}>
              <LuX size={18} />
            </button>
            <p className={styles.modalMessage}>Are you sure you want to log out?</p>
            <div className={styles.modalActions}>
              <button className={styles.btnCancel} onClick={() => setLogoutModal(false)}>Cancel</button>
              <button className={styles.btnLogout} onClick={handleLogout}>Log out</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
